import React, { useState } from "react";
import {
  doc,
  DocumentReference,
  FieldValue,
  updateDoc,
} from "firebase/firestore";
import { useTranslation } from "react-i18next";
import { useFirestore } from "reactfire";

import { classNames } from "../../utils/classNames";
import { DsButton } from "../ui/DsButton";

type ServiceType = {
  id: string;
  [key: string]: unknown;
};

type EditableFieldPropsType = {
  path: string | string[];
  fieldName: string;
  initialValue: unknown;
  display: React.ReactNode;
  control: React.ComponentType<{
    /* eslint-disable-next-line */
    value: any;
    onChange: (value: unknown) => void;
    onBlur?: () => void;
  }>;
  hoverEdit?: boolean;
  saveOnChange?: boolean;
  saveOnBlur?: boolean;
  editType?: "connect" | "serviceDate";
  services?: ServiceType | ServiceType[];
  onSuccess?: () => void;
  hideEditButton?: boolean;
};

export const EditableField: React.FC<EditableFieldPropsType> = ({
  path,
  fieldName,
  initialValue,
  display,
  control: Control,
  hoverEdit,
  saveOnChange,
  saveOnBlur,
  editType,
  services,
  onSuccess,
  hideEditButton = false,
}) => {
  const { t } = useTranslation();
  const firestore = useFirestore();
  const docs: DocumentReference[] = [];

  if (Array.isArray(path)) {
    path.forEach(p => {
      docs.push(doc(firestore, p));
    });
  } else {
    docs.push(doc(firestore, path));
  }

  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState<unknown>(initialValue);

  const isHidden = saveOnBlur ?? saveOnChange;

  const matchedService =
    typeof services === "object" && !Array.isArray(services)
      ? services
      : Array.isArray(services)
        ? services.find((service: ServiceType) => service.id === value)
        : undefined;

  const handleSubmit = (): void => {
    const updateDocs = async (): Promise<void> => {
      await Promise.all(
        docs.map(async docRef => {
          const updateData = {
            [fieldName]: editType === "connect" ? matchedService : value,
          } as Record<string, FieldValue | Partial<unknown> | undefined>;

          await updateDoc(docRef, updateData);

          if (editType === "serviceDate") {
            await updateDoc(docRef, {
              date: value,
            });
          }
        }),
      );

      onSuccess?.();
      setIsEditing(false);
    };

    void updateDocs();
  };

  const handleChange = (newValue: unknown): void => {
    setValue(newValue);
    if (saveOnChange) {
      const updateDocs = async (): Promise<void> => {
        await Promise.all(
          docs.map(async docRef => {
            const updateData = {
              [fieldName]: newValue,
            } as Record<string, FieldValue | Partial<unknown> | undefined>;
            await updateDoc(docRef, updateData);
          }),
        );
        onSuccess?.();
      };

      void updateDocs();
    }
  };

  if (isHidden) {
    return (
      <Control
        value={value ?? ""}
        onChange={handleChange}
        onBlur={saveOnBlur ? handleSubmit : undefined}
      />
    );
  }

  return (
    <div className={!isHidden ? "flex gap-2 group flex-wrap" : ""}>
      {isEditing ? (
        <Control
          value={value ?? ""}
          onChange={handleChange}
          onBlur={saveOnBlur ? handleSubmit : undefined}
        />
      ) : (
        display
      )}
      <div className="ml-auto flex gap-2">
        {isEditing && (
          <DsButton
            onClick={handleSubmit}
            variant="primary"
            size="primary"
            className="block py-1 px-2 text-xs uppercase h-6">
            {t("save")}
          </DsButton>
        )}
        {!isHidden && !hideEditButton && (
          <DsButton
            onClick={() => setIsEditing(!isEditing)}
            variant={isEditing ? "secondary" : "primary"}
            size="primary"
            className={classNames(
              hoverEdit && !isEditing ? "hidden group-hover:block" : "block",
              "block py-1 px-2 text-xs uppercase h-6",
            )}>
            {isEditing ? t("cancel") : t("edit")}
          </DsButton>
        )}
      </div>
    </div>
  );
};
